import { createSelector } from 'reselect';
import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import memoize from 'lodash/memoize';
import uniq from 'lodash/uniq';
import {
	ISSUE_HIERARCHY_LEVEL_EPIC,
	ISSUE_HIERARCHY_LEVEL_BASE,
} from '@atlassian/jira-issue-type-hierarchies';
import { DEFAULT_PARENT_ISSUE_TYPE_NAME } from '../../../model/constants';
import type { IssueParent } from '../../../model/issue-parent/types';
import type { IssueParentsEntityState } from '../../reducers/entities/issue-parent/types';
import type { State } from '../../types';
import { getEntities, getIssueParentsOrderIds } from '../entities';
import { getAppliedParentFilters } from '../filters';

export const getIssueParents = (state: State): IssueParentsEntityState =>
	getEntities(state).issueParents;

export const getIssueParent = createSelector([getIssueParents], (issueParents) => {
	const issueParentById = (parentId: number): IssueParent | null =>
		issueParents[String(parentId)] || null;

	return memoize(issueParentById);
});

export const issueParentsSelector = createSelector(
	[getIssueParents, getIssueParentsOrderIds],
	(issueParents, issueParentsOrderIds) =>
		issueParentsOrderIds.reduce<IssueParent[]>((issueParentsArray, issueParentId) => {
			const issueParent = issueParents[issueParentId];
			issueParent && issueParentsArray.push(issueParent);
			return issueParentsArray;
		}, []),
);

export const issueParentsToListItemSelector = createSelector(issueParentsSelector, (issueParents) =>
	issueParents.map(({ id, summary }) => ({
		id: String(id),
		name: summary,
	})),
);

export const issueParentIdsSelector = createSelector(getIssueParents, (issueParents) =>
	Object.keys(issueParents),
);

export const partialIssueParentSelector = createSelector(issueParentsSelector, (issueParents) =>
	issueParents.map(({ id, summary, issueType, key }) => ({
		id,
		summary,
		key,
		issueType,
	})),
);

export const issueParentTypeIdsSelector = createSelector(issueParentsSelector, (issueParents) =>
	uniq(issueParents.map(({ issueType }) => issueType.id)),
);

export const firstParentIssueTypeNameSelector = createSelector(
	issueParentsSelector,
	(issueParents) =>
		issueParents
			? get(first(issueParents), ['issueType', 'name'], DEFAULT_PARENT_ISSUE_TYPE_NAME)
			: DEFAULT_PARENT_ISSUE_TYPE_NAME,
);

export const filteredParentsSelector = createSelector(
	getAppliedParentFilters,
	getIssueParents,
	(filteredParentIds, issueParents) =>
		!isEmpty(filteredParentIds)
			? filteredParentIds
					?.map((filteredParentId) => issueParents?.[filteredParentId]) // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					.filter(Boolean) || ([] as IssueParent[])
			: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				([] as IssueParent[]),
);

// @ts-expect-error - TS2769 - No overload matches this call.
export const issueHierarchyLevelSelector = createSelector([], () => {
	const getIssueHierarchyLevel = (isEpicPanel = false): number =>
		isEpicPanel ? ISSUE_HIERARCHY_LEVEL_EPIC : ISSUE_HIERARCHY_LEVEL_BASE;

	return getIssueHierarchyLevel;
});
